import {
  Body,
  Box,
  buttonLinkG4P,
  CheckboxField,
  Divider,
  Form,
  FormRow,
  FormSection,
  LoadingButton,
  PositiveInteger,
  Space,
  Stack,
  useComponentsWrapperContext,
  useForm,
  useIsMobileLayout,
  validators,
} from "@dbn/omnichannel-design-system-g4p";
import { useFormatMessage } from "../../../intl";
import { boolean, either, option, taskEither } from "fp-ts";
import { ReadOnlyMoneyAmountField } from "../../../Common/ReadOnlyMoneyAmountField/ReadOnlyMoneyAmountField";
import { MoneyField } from "../../../Common/Price/MoneyField/MoneyField";
import { foldTenant, MoneyAmount } from "../../../globalDomain";
import { Option } from "fp-ts/Option";
import { useTenantCurrency } from "../../../Common/useTenantCurrency";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  constFalse,
  constNull,
  constUndefined,
  constVoid,
  flow,
  pipe,
} from "fp-ts/function";
import { YesNoRadioGroupField } from "../../../Common/YesNoRadioGroup/YesNoRadioGroupField";
import { useAppContext } from "../../../useAppContext";
import { SubmitFormState } from "./ExpensesConfirmation";
import { useUpdateEffect } from "react-use";
import cx from "classnames";
import { useCommand } from "../../../useAPI";
import {
  ExpensesCalculatorData,
  saveRegularMonthlyExpensesNoLiabilities,
  submitExpenses,
  SubmitExpensesInput,
} from "./api";
import * as classes from "./ExpensesConfirmation.css";
import { useValidators } from "../../../Common/useValidators";

type Props = {
  regularMonthlyExpenses: MoneyAmount;
  calculatorData: Option<ExpensesCalculatorData>;
  setIsLiabilitiesDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsExpensesDialogOpen: Dispatch<SetStateAction<boolean>>;
  submitFormState: SubmitFormState;
  isCalculatorUsed: boolean;
  onFormSubmit: Dispatch<SetStateAction<boolean>>;
  readOnly: boolean;
};

export function ExpensesConfirmationFormRework(props: Props) {
  const formatMessage = useFormatMessage();
  const tenantCurrency = useTenantCurrency();
  const [totalMonthlyExpenses, setTotalMonthlyExpenses] = useState({
    amount: 0,
    currency: tenantCurrency,
  });

  const [
    regularMonthlyExpensesNoLiabilities,
    setRegularMonthlyExpensesNoLiabilities,
  ] = useState<Option<number>>(option.none);

  const { componentType } = useComponentsWrapperContext();

  const {
    apiParameters: { tenant },
  } = useAppContext();

  const { definedNoExtract, positiveInteger } = useValidators();

  const { fieldProps, handleSubmit, values, handleReset } = useForm(
    {
      initialValues: { ...props.submitFormState },
      fieldValidators: ({ payAlimony }) => ({
        disclaimerCheckbox: validators.checked(
          formatMessage(
            "StandardLoan.ExpensesConfirmation.Form.checkboxFieldError"
          )
        ),
        totalRegularMonthlyExpensesNoLiabilities: validators.inSequence(
          definedNoExtract<number>(),
          validators.validateIfDefined(positiveInteger)
        ),
        payAlimony: foldTenant(tenant, definedNoExtract, constUndefined),
        alimonyAmount: foldTenant(
          tenant,
          () =>
            validators.validator<Option<number>, Option<PositiveInteger>>(
              value =>
                pipe(
                  payAlimony,
                  option.fold(
                    () => either.right(option.none),
                    boolean.fold(
                      () => either.right(option.none),
                      () =>
                        pipe(
                          value,
                          option.fold(
                            () =>
                              either.left(
                                formatMessage("Form.fieldError.positiveInteger")
                              ),
                            flow(
                              PositiveInteger.decode,
                              either.bimap(
                                () =>
                                  formatMessage(
                                    "Form.fieldError.positiveInteger"
                                  ),
                                option.some
                              )
                            )
                          )
                        )
                    )
                  )
                )
            ),
          constUndefined
        ),
      }),
    },
    {
      onSubmit: data =>
        pipe(
          submitExpensesForm({
            ...data,
            totalRegularMonthlyExpensesNoLiabilities: {
              amount: pipe(
                props.isCalculatorUsed
                  ? props.submitFormState
                      .totalRegularMonthlyExpensesNoLiabilities
                  : regularMonthlyExpensesNoLiabilities,
                option.fold(
                  () => 0,
                  value => value
                )
              ),
              currency: tenantCurrency,
            },
          } as SubmitExpensesInput),
          taskEither.chain(() =>
            taskEither.fromIO(() => {
              props.onFormSubmit(true);
            })
          )
        ),
    }
  );

  useEffect(() => {
    const totalMonthlyExpensesNoLiabilities = fieldProps(
      "totalRegularMonthlyExpensesNoLiabilities"
    ).value;
    const alimonyAmount = fieldProps("alimonyAmount").value;
    const totalActualExpenses =
      props.regularMonthlyExpenses.amount +
      pipe(
        alimonyAmount,
        option.fold(
          () => 0,
          value => value
        )
      ) +
      pipe(
        totalMonthlyExpensesNoLiabilities,
        option.fold(
          () => 0,
          value => value
        )
      );
    setTotalMonthlyExpenses({
      ...totalMonthlyExpenses,
      amount: totalActualExpenses,
    });
    setRegularMonthlyExpensesNoLiabilities(totalMonthlyExpensesNoLiabilities);
  }, [
    fieldProps("totalRegularMonthlyExpensesNoLiabilities").value,
    fieldProps("alimonyAmount").value,
    props.regularMonthlyExpenses.amount,
  ]);

  useEffect(() => {
    const payAlimony = pipe(
      fieldProps("payAlimony").value,
      option.getOrElse(constFalse)
    );

    if (!payAlimony) {
      fieldProps("alimonyAmount").onChange(option.none as Option<number>);
    }
  }, [fieldProps("payAlimony").value]);

  useUpdateEffect(handleReset, [JSON.stringify(props.submitFormState)]);

  const submitExpensesForm = useCommand(submitExpenses);

  const saveRegularMonthlyExpenses = useCommand(
    saveRegularMonthlyExpensesNoLiabilities
  );

  const isMobileLayout = useIsMobileLayout();

  return (
    <Form shrink>
      <FormSection
        heading={{
          title: formatMessage(
            "StandardLoan.ExpensesConfirmationReword.Form.title"
          ),
          titleWeight: "medium",
          titleSize: "small",
        }}
      >
        <FormRow type="1-1">
          <MoneyField
            description={formatMessage(
              "StandardLoan.ExpensesConfirmationRework.Form.regularMonthlyExpensesInputToolTip"
            )}
            {...fieldProps("totalRegularMonthlyExpensesNoLiabilities")}
            disabled={props.isCalculatorUsed}
            min={0}
            placeholder={formatMessage(
              "StandardLoan.ExpensesConfirmationRework.Form.regularMonthlyExpensesInputPlaceholder"
            )}
            currency={tenantCurrency}
            label={formatMessage(
              "StandardLoan.ExpensesConfirmationRework.Form.regularMonthlyExpensesInputLabel"
            )}
          />
          <div
            style={{
              display: "flex",
              flex: "auto",
              alignItems: "end",
              maxHeight: "75px",
            }}
          >
            <Body size="medium" weight="regular">
              {buttonLinkG4P(
                () => {
                  props.setIsExpensesDialogOpen(true);
                },
                formatMessage(
                  props.isCalculatorUsed
                    ? "StandardLoan.ExpensesConfirmation.Panel.editCalculator"
                    : "StandardLoan.ExpensesConfirmation.Panel.useCalculator"
                ),
                formatMessage(
                  props.isCalculatorUsed
                    ? "StandardLoan.ExpensesConfirmation.Panel.editCalculator.linkPurpose"
                    : "StandardLoan.ExpensesConfirmation.Panel.useCalculator.linkPurpose"
                )
              )}
            </Body>
          </div>
        </FormRow>
        <FormRow type="1-1">
          <ReadOnlyMoneyAmountField
            description={formatMessage(
              "StandardLoan.ExpensesConfirmationRework.Form.totalLiabilitiesToolTip"
            )}
            size="x-small"
            value={props.regularMonthlyExpenses}
            label={formatMessage(
              "StandardLoan.ExpensesConfirmationRework.Form.totalLiabilitiesLabel"
            )}
          />
          <Box hAlignContent="left">
            <Body size="medium" weight="regular">
              {[
                buttonLinkG4P(
                  () => {
                    const totalMonthlyExpensesNoLiabilities = fieldProps(
                      "totalRegularMonthlyExpensesNoLiabilities"
                    ).value;
                    pipe(
                      saveRegularMonthlyExpenses({
                        amount: pipe(
                          totalMonthlyExpensesNoLiabilities,
                          option.fold(
                            () => 0,
                            value => value
                          )
                        ),
                        currency: tenantCurrency,
                      } as MoneyAmount)()
                    );
                    props.setIsLiabilitiesDialogOpen(true);
                  },
                  formatMessage(
                    "StandardLoan.ExpensesConfirmation.Panel.seeDetailCTA"
                  ),
                  formatMessage(
                    "Overdraft.editExpense.loanOverview.linkPurpose"
                  )
                ),
              ]}
            </Body>
          </Box>
        </FormRow>
        {foldTenant(
          tenant,
          () => (
            <>
              <FormRow type="full">
                <YesNoRadioGroupField
                  {...fieldProps("payAlimony")}
                  label={formatMessage(
                    "StandardLoan.ExpensesConfirmation.Form.alimonyLabel"
                  )}
                  description={formatMessage(
                    "StandardLoan.ExpensesConfirmation.Form.alimonyDescription"
                  )}
                  onBlur={constVoid}
                />
              </FormRow>
              <Space units={8} />
              {pipe(
                values.payAlimony,
                option.fold(constNull, value =>
                  pipe(
                    value,
                    boolean.fold(constNull, () => (
                      <FormRow type="1-1">
                        <MoneyField
                          {...fieldProps("alimonyAmount")}
                          min={1}
                          max={999999999}
                          currency={tenantCurrency}
                          placeholder={formatMessage(
                            "StandardLoan.ExpensesConfirmation.Form.alimonyAmountPlaceholder"
                          )}
                          label={formatMessage(
                            "StandardLoan.ExpensesConfirmation.Form.alimonyAmountLabel"
                          )}
                        />
                        <></>
                      </FormRow>
                    ))
                  )
                )
              )}
            </>
          ),
          constNull
        )}
        <FormRow type="full">
          <Divider />
        </FormRow>
        <FormRow type="full">
          <ReadOnlyMoneyAmountField
            size="x-small"
            value={totalMonthlyExpenses}
            label={formatMessage(
              "StandardLoan.ExpensesConfirmationRework.Panel.totalMonthlyExpenses"
            )}
          />
        </FormRow>
        <FormRow type="full">
          <CheckboxField
            {...fieldProps("disclaimerCheckbox")}
            label={formatMessage(
              "StandardLoan.ExpensesConfirmation.Panel.checkbox"
            )}
          />
        </FormRow>
        <FormRow type="full">
          <Stack
            hAlignContent={isMobileLayout ? "center" : "right"}
            column={isMobileLayout}
          >
            <></>
            <LoadingButton
              className={cx(
                isMobileLayout &&
                  (!componentType || componentType === "g4p") &&
                  classes.submitExpensesButtonMobile
              )}
              disabled={!props.readOnly}
              type="submit"
              variant="primary"
              labels={{
                normal: formatMessage("Confirm.buttonLabel"),
                success: formatMessage("Confirm.buttonLabel"),
                loading: formatMessage("Loading"),
                error: formatMessage("Confirm.buttonLabel"),
              }}
              size="default"
              action={handleSubmit}
            />
          </Stack>
        </FormRow>
      </FormSection>
    </Form>
  );
}
